import React, { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';

import { Carousel } from 'components/Carousel';
import { getPixleeContentFromSku } from 'features/pixlee/api';
import {
	dateMillisToDateString, getSource, isInstagramSource, isTurntoSource
} from 'features/pixlee/helpers';
// use existing pixlee PDP config for calling their API
import { albumDetailsForPDP } from 'apps/pixlee/constants';
import { utagLinkPDP } from 'features/tealium';
import { MediaModalButtonGroup, MediaYouTube } from 'components/Media';

import './MediaModal.scss';

const noSliderTransitionsClass = 'MediaModalContent__sliderWrapper--noTransitions';
let xDown = null;
let yDown = null;

const MediaModal = (props) => {

	const {
		productSku, activeIndex, list, includeAllCustomerMedia, showSocialCaptions, showTurntoCaptions
	} = props;
	const [ mediaList, setMediaList ] = useState(list);
	// set active item to display
	const [ activeItemInfo, setActiveItemInfo ] = useState(list[activeIndex]);
	const carouselActiveIndex = activeIndex;
	const initSliderOptions = {
		responsive: {
			superLargeDesktop: {
				breakpoint: { max: 4000, min: 3000 },
				items: 1
			},
			desktop: {
				breakpoint: { max: 3000, min: 1024 },
				items: 1
			},
			tablet: {
				breakpoint: { max: 1024, min: 464 },
				items: 1
			},
			mobile: {
				breakpoint: { max: 464, min: 0 },
				items: 1
			}
		},
		slidesToSlide: 1,
		arrows: false,
		swipeable: true,
		draggable: false,
		showDots: false,
		containerClass: 'MediaModalContent__slider',
		itemClass: 'MediaModalContent__slide',
		renderButtonGroupOutside: true,
		customTransition: 'none'
	};
	const [ sliderOpts, setSlideOpts ] = useState(initSliderOptions);
	const sliderEl = useRef();
	const prevScrollY = useRef(0);
	const [ isScrollBarUp, setScrollBarUp ] = useState(true);
	const scrollDivRef = useRef();
	const [ activeImageIndex, setActiveImageIndex ] = useState(1);

	const handleModalScroll = (e) => {

		const currentScrollY = e.target.scrollTop;
		if (prevScrollY.current < currentScrollY && isScrollBarUp) {

			setScrollBarUp(false);
			utagLinkPDP('TurnTo')({
				eventAction: 'ReviewImageModal-Scroll',
				eventLabel: `${productSku}-${activeImageIndex}`
			});

		}
		prevScrollY.current = currentScrollY;

	};

	const handleSlideChange = (slideIndex) => {

		setActiveItemInfo(mediaList[slideIndex]);
		if (scrollDivRef.current) {

			scrollDivRef.current.scrollTo(0, 0);

		}
		setScrollBarUp(true);
		slideIndex += 1;
		setActiveImageIndex(slideIndex);
		if (activeImageIndex < slideIndex) {

			utagLinkPDP('TurnTo')({
				eventAction: 'ReviewImageModal-Forward',
				eventLabel: `${productSku}-${slideIndex}`
			});

		} else {

			utagLinkPDP('TurnTo')({
				eventAction: 'ReviewImageModal-Back',
				eventLabel: `${productSku}-${slideIndex}`
			});

		}

	};

	const handleVideoPlay = async (videoId) => {

		let eventLabel = videoId;
		if (activeItemInfo && activeItemInfo.youtubeTitle) {

			eventLabel = `${eventLabel}-${activeItemInfo.youtubeTitle}`;

		} else {

			const response = await fetch(
				`https://www.youtube-nocookie.com/oembed?format=json&url=https://www.youtube-nocookie.com/watch?v=${videoId}`,
				{
					method: 'GET'
				}
			);
			if (!response.ok) {

				throw Error;

			}
			const videoInfo = await response.json();
			if (videoInfo) {

				setActiveItemInfo({ ...activeItemInfo, youtubeTitle: videoInfo.title });
				eventLabel = `${eventLabel}-${videoInfo.title}`;

			}

		}

		utagLinkPDP('TurnTo')({
			eventAction: 'ReviewImageModal-PlayVideo',
			eventLabel
		});

	};

	const handleTouchStart = (e) => {

		const firstTouch = e.touches[0] || e.originalEvent.touches[0];
		xDown = firstTouch.clientX;
		yDown = firstTouch.clientY;

	};
	const handleTouchMove = (e) => {

		if (!sliderEl.current.classList.contains(noSliderTransitionsClass) || !xDown || !yDown) return;
		const xUp = e.touches[0].clientX;
		const yUp = e.touches[0].clientY;
		const xDiff = xDown - xUp;
		const yDiff = yDown - yUp;
		const isSwipeX = Math.abs(xDiff) > Math.abs(yDiff);

		if (isSwipeX) {

			sliderEl.current.classList.remove(noSliderTransitionsClass);

		} else {

			sliderEl.current.classList.add(noSliderTransitionsClass);

		}
		xDown = null;
		yDown = null;

	};

	// call Pixlee api on component load
	useEffect(() => {

		let didCancel = false;

		async function fetchPixleeContentFromSku () {

			try {

				const res = await getPixleeContentFromSku({ ...albumDetailsForPDP, productSku });
				if (res.status !== 200) {

					throw Error;

				}
				if (res.data && res.data.length && res.status === 200) {

					const pixleeItems = res.data
						// remove duplicate media content
						.filter((pixleeItem) => !list.find((item) => item.author === pixleeItem.user_name))
						.map((pixleeItem) => {

							let caption = pixleeItem.title;

							if (
								(!showSocialCaptions && isInstagramSource(pixleeItem.source))
								|| (!showTurntoCaptions && isTurntoSource(pixleeItem.source, pixleeItem.email_address))
							) {

								caption = null;

							}

							return {
								id: pixleeItem.id,
								media: {
									src: pixleeItem.pixlee_cdn_photos.square_medium_url,
									type: 'photo'
								},
								// no title returned from API response
								title: null,
								text: !!caption && !isInstagramSource(pixleeItem.source) ? caption : null,
								instagramText: !!caption && isInstagramSource(pixleeItem.source) ? caption : null,
								author: pixleeItem.user_name,
								date: dateMillisToDateString(pixleeItem.submitted_at),
								source: getSource(pixleeItem.source, pixleeItem.email_address),
								type: pixleeItem.source.toLowerCase() === 'youtube' ? 'video' : 'image'
							};

						});

					if (!didCancel) {

						setMediaList((prev) => [ ...prev, ...pixleeItems ]);

					}

				}

			} catch (err) {

				// eslint-disable-next-line no-console
				console.error('Error fetching Pixlee API');

			}

		}

		if (includeAllCustomerMedia) {

			fetchPixleeContentFromSku();

		}

		return () => {

			didCancel = true;

		};

	}, [ list ]);

	useEffect(() => {

		function handleOrientationChange () {

			sliderEl.current.classList.add(noSliderTransitionsClass);

		}

		setTimeout(() => {

			setSlideOpts({
				...sliderOpts,
				customTransition: 'transform 300ms ease-in-out'
			});

		}, 500);

		window.addEventListener('orientationchange', handleOrientationChange);
		return () => {

			window.removeEventListener('orientationchange', handleOrientationChange);

		};

	}, []);

	return (
		<main className="MediaModalContent">
			<div
				className="MediaModalContent__sliderWrapper"
				ref={sliderEl}
				onTouchStart={handleTouchStart}
				onTouchMove={handleTouchMove}
			>
				<Carousel
					{...sliderOpts}
					beforeChange={() => {

						if (sliderEl.current) {

							sliderEl.current.classList.remove(noSliderTransitionsClass);

						}

					}}
					afterChange={(previousSlide, { currentSlide }) => {

						setActiveItemInfo(mediaList[currentSlide]);
						setActiveImageIndex(currentSlide + 1);

					}}
					customButtonGroup={(
						<MediaModalButtonGroup
							activeSlideIndex={carouselActiveIndex}
							onSlideChange={(slideIndex) => {

								handleSlideChange(slideIndex);

							}}
						/>
					)}
				>
					{mediaList.map((item) => {

						const { media: mediaItem } = item;

						if (mediaItem.type === 'video' && mediaItem.videoSrc) {

							return (
								<MediaYouTube
									key={item.id}
									videoSrc={mediaItem.videoSrc}
									handleVideoPlay={(videoId) => {

										handleVideoPlay(videoId);

									}}
									isActive={item.id === activeItemInfo.id}
								/>
							);

						}
						return <img key={item.id} src={mediaItem.src} alt="" />;

					})}
				</Carousel>
			</div>
			{activeItemInfo && (
				<div className="MediaModalContent__info" ref={scrollDivRef} onScroll={handleModalScroll}>
					{activeItemInfo.title && (
						<div className="MediaModalContent__title">
							{activeItemInfo.title}
						</div>
					)}
					{activeItemInfo.text && (
						<p className="MediaModalContent__text">
							{activeItemInfo.text}
						</p>
					)}
					{activeItemInfo.instagramText && (
						<p className="MediaModalContent__instagramText">
							{activeItemInfo.instagramText}
						</p>
					)}
					<p className="MediaModalContent__source">
						{activeItemInfo.author}
						{activeItemInfo.source && ` | ${activeItemInfo.source}`}
					</p>
					{activeItemInfo.date !== null && (
						<p className="MediaModalContent__date">
							{activeItemInfo.date}
						</p>
					)}
				</div>
			)}
		</main>
	);

};

MediaModal.defaultProps = {
	includeAllCustomerMedia: true
};

MediaModal.propTypes = {
	productSku: PropTypes.string.isRequired,
	activeIndex: PropTypes.number.isRequired,
	list: PropTypes.arrayOf(PropTypes.any).isRequired,
	onClose: PropTypes.func,
	includeAllCustomerMedia: PropTypes.bool,
	showTurntoCaptions: PropTypes.bool,
	showSocialCaptions: PropTypes.bool
};

export default MediaModal;
